import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Container from 'reactstrap/lib/Container';
import Card from 'reactstrap/lib/Card';
import { get } from 'lodash-es';

import { useOnScreen } from 'site-modules/shared/hooks/use-on-screen';
import { getCorrelatorTs } from 'client/engagement-handlers/helper';
import { AdLabel } from 'site-modules/shared/components/native-ad/native-ad-components/ad-label/ad-label';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { withSiteServedAdWrapper } from 'site-modules/shared/components/native-ad/site-served-ad-wrapper/site-served-ad-wrapper';
import { siteServedAdTrackingWrapper } from 'site-modules/shared/components/native-ad/site-served-ad-tracking-wrapper/site-served-ad-tracking-wrapper';
import {
  AdComponentDefaultProps,
  AdComponentPropTypes,
  SiteServedAdPropTypes,
} from 'site-modules/shared/components/ad-unit/ad-unit-prop-types';
import {
  EasListingCreativeConfigDefaultProps,
  EasListingCreativeConfigPropTypes,
} from 'client/data/models/native-ads-creative-config';
import { DEALER_CLICKOUT_CUSTOM } from 'site-modules/shared/constants/ads';
import { EAS_LISTING_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { numberWithPluralText } from 'site-modules/shared/utils/string';
import { getM2ForSaleUrl } from 'site-modules/shared/utils/inventory/get-m2-for-sale-url';

import './eas-listing-ad.scss';

const CARD_WIDTH = 260;
const CTA = 'View Dealer Offer';

function KeyPointSimilarCard({ icon, iconTitle, content, classes, className }) {
  return (
    <li className={classnames('size-14 col-12 p-0', className)}>
      <span title={iconTitle} className={classnames(icon, classes, 'key-point-icon d-inline-block size-12 me-0_5')} />
      {content}
    </li>
  );
}

KeyPointSimilarCard.propTypes = {
  icon: PropTypes.string.isRequired,
  iconTitle: PropTypes.string.isRequired,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  classes: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
};

function EasListingUI({
  adRef,
  adClickTrackingListener,
  adLoadTrackingListener,
  adTrackers,
  creativeConfig: { creativeConfigData } = EasListingCreativeConfigDefaultProps,
  position,
  wrapperClass = null,
  cardClassName = 'border-0',
  ivtClassName,
}) {
  const { cddId, ctucId, dealerName } = creativeConfigData;
  const vehicles = get(creativeConfigData, 'vehicles', []);

  const [isIntersecting, disconnect] = useOnScreen(adRef, { rootMargin: '0px' });

  function onClick() {
    adClickTrackingListener({
      eventTrackingId: DEALER_CLICKOUT_CUSTOM,
      customLineItemId: cddId,
      creativeConfigId: ctucId,
    });
  }

  useEffect(() => {
    if (isIntersecting) {
      adLoadTrackingListener(null, CTA, getCorrelatorTs(), cddId, { creative_config_id: ctucId });
      disconnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIntersecting]);

  const nativeStyle = EAS_LISTING_AD.NATIVE_STYLE;

  return (
    <div
      className={classnames(EAS_LISTING_AD.AD_NAME, wrapperClass, ivtClassName)}
      data-tracking-parent={nativeStyle}
      data-viewability={`${nativeStyle}-${position}`}
      ref={adRef}
    >
      {adTrackers}
      <div className="eas-listing-card-list pb-0_5 flex-nowrap g-0 w-100 h-100 row">
        {vehicles.map((vehicle, index) => (
          <Card
            className={classnames('eas-listing-card bg-white rounded-12 overflow-hidden col-auto', cardClassName)}
            key={vehicle.clickOutUrl}
            style={{ width: CARD_WIDTH }}
            data-eas-card-position={index}
          >
            <a
              href={vehicle.clickOutUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClick}
              data-tracking-value={vehicle.clickOutUrl}
              data-tracking-position={index}
              className="site-served-ad-link no-focus text-decoration-none d-flex flex-column h-100"
            >
              <AspectRatioContainer aspectRatio="3:2">
                <img src={getM2ForSaleUrl(vehicle.vehicle_image)} alt="" />
              </AspectRatioContainer>
              <AdLabel />
              <div style={{ fontSize: '26px' }} className="px-1 mt-1 text-gray-darkest fw-bold">
                {vehicle.price.split('.')[0]}
              </div>
              <div className="size-16 px-1 mt-0_5 fw-bold text-primary-darker">
                {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.bodyStyle} {vehicle.trim}
                <span className="visually-hidden">&nbsp;year make model body-style trim</span>
              </div>
              <div className="px-1 mt-auto">
                <ul className="list-unstyled mb-1 text-cool-gray-10" aria-label="At a glance">
                  {Number(vehicle.mileage) !== 0 && (
                    <KeyPointSimilarCard
                      icon="icon-meter"
                      iconTitle="vehicle mileage"
                      content={numberWithPluralText(Number(vehicle.mileage), 'mile', true)}
                      classes=""
                      className="mt-0_75"
                    />
                  )}
                  <KeyPointSimilarCard
                    icon="icon-location"
                    iconTitle="dealer name"
                    content={dealerName}
                    classes=""
                    className="mt-0_25"
                  />
                </ul>
                <div className="mb-1">
                  <div
                    className={classnames(
                      'w-100 btn text-nowrap py-0_5 px-0_75 text-transform-none size-16 btn-blue-50 rounded-8 fw-medium'
                    )}
                  >
                    {CTA}
                    <span className="icon-open_in_new size-12 ms-0_25" aria-hidden />
                  </div>
                </div>
              </div>
            </a>
          </Card>
        ))}
      </div>
    </div>
  );
}

EasListingUI.propTypes = {
  ...SiteServedAdPropTypes,
  creativeConfig: EasListingCreativeConfigPropTypes,
  wrapperClass: PropTypes.string,
  cardClassName: PropTypes.string,
};

function EasListingContainer({ similarVehicleTitle, ...restProps }) {
  return similarVehicleTitle ? (
    <div className={classnames('vdp-similar-vehicles bg-blue-90 overlay-white-40 pt-2 mb-2 mt-2 pb-1')}>
      <Container>
        <h2 className="heading-3 mb-1_5">{similarVehicleTitle}</h2>
        <EasListingUI {...restProps} />
      </Container>
    </div>
  ) : (
    <EasListingUI {...restProps} />
  );
}

const EasListingWrapper = withSiteServedAdWrapper(siteServedAdTrackingWrapper(EasListingContainer));

export function EasListingAd({
  position = '1',
  similarVehicleTitle = null,
  mobile = AdComponentDefaultProps.mobile,
  slotRenderEndListener = AdComponentDefaultProps.slotRenderEndListener,
  placeholderComponent = AdComponentDefaultProps.placeholderComponent,
  placeholderComponentProps = AdComponentDefaultProps.placeholderComponentProps,
  customTargeting = AdComponentDefaultProps.customTargeting,
  vehicle = AdComponentDefaultProps.vehicle,
  currentVehicle = AdComponentDefaultProps.currentVehicle,
  dependedOnCurrentVehicle = AdComponentDefaultProps.dependedOnCurrentVehicle,
  wrapperClass = AdComponentDefaultProps.wrapperClass,
  siteServedAdsEnabled = AdComponentDefaultProps.siteServedAdsEnabled,
  breakpoints = AdComponentDefaultProps.breakpoints,
  isIvt = AdComponentDefaultProps.isIvt,
  ivtClassName = AdComponentDefaultProps.ivtClassName,
  ...restProps
}) {
  return (
    <EasListingWrapper
      position={position}
      similarVehicleTitle={similarVehicleTitle}
      mobile={mobile}
      slotRenderEndListener={slotRenderEndListener}
      placeholderComponent={placeholderComponent}
      placeholderComponentProps={placeholderComponentProps}
      customTargeting={customTargeting}
      vehicle={vehicle}
      currentVehicle={currentVehicle}
      dependedOnCurrentVehicle={dependedOnCurrentVehicle}
      wrapperClass={wrapperClass}
      siteServedAdsEnabled={siteServedAdsEnabled}
      breakpoints={breakpoints}
      isIvt={isIvt}
      ivtClassName={ivtClassName}
      {...restProps}
      adType={EAS_LISTING_AD}
    />
  );
}

EasListingAd.propTypes = {
  ...AdComponentPropTypes,
  position: PropTypes.string,
  similarVehicleTitle: PropTypes.string,
};
